import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "hiddenInput", "results", "resultsList", "loading", "initialMessage", "errorMessage", "resultLink"]
  static values = {
    url: String,
    field: String,
    formMode: Boolean,
    guestSelector: Boolean,
    submitParentForm: Boolean
  }

  connect() {
    this.timeout = null
    this.blurTimeout = null
    this.selectedIndex = -1
    this.isSelecting = false
    this.hasSearched = false
    this.searchInFlight = false

    this.clickListener = (ev) => {
      if (!this.element.contains(ev.target) && !this.isSelecting) {
        this.hideResults()
      }
    }

    this.repositionListener = () => {
      if (!this.resultsTarget.classList.contains("hidden")) {
        this.positionResults()
      }
    }

    this.keydownListener = (ev) => {
      if (ev.key === "Escape") {
        this.hideResults()
        this.searchInputTarget.blur()
      } else if (ev.key === "ArrowDown") {
        ev.preventDefault()
        this.navigateResults(1)
      } else if (ev.key === "ArrowUp") {
        ev.preventDefault()
        this.navigateResults(-1)
      } else if (ev.key === "Enter") {
        ev.preventDefault()
        if (this.selectedIndex >= 0) {
          this.selectCurrentResult()
        }
      }
    }

    window.addEventListener("click", this.clickListener)
    window.addEventListener("resize", this.repositionListener)
    window.addEventListener("scroll", this.repositionListener, true)
    this.searchInputTarget.addEventListener("keydown", this.keydownListener)
  }

  disconnect() {
    window.removeEventListener("click", this.clickListener)
    window.removeEventListener("resize", this.repositionListener)
    window.removeEventListener("scroll", this.repositionListener, true)
    if (this.timeout) clearTimeout(this.timeout)
    if (this.searchInputTarget) {
      this.searchInputTarget.removeEventListener("keydown", this.keydownListener)
    }
  }

  search(event) {
    const query = event.target.value.trim()

    clearTimeout(this.timeout)

    if (this.formModeValue && this.hasHiddenInputTarget && this.hiddenInputTarget.value) {
      this.hiddenInputTarget.value = ""
      this.element.dispatchEvent(new CustomEvent("user-search:cleared", { bubbles: true }))
    }

    if (query.length < 2) {
      this.hideResults()
      this.hasSearched = false
      return
    }

    this.hasSearched = true
    this.showResults()
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    this.searchInFlight = true
    this.showLoading()

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("query", query)
    if (this.formModeValue || this.guestSelectorValue) {
      url.searchParams.set("form_mode", "1")
    }
    const guestContainer = this.element.closest("[data-controller~='lesson-guest-selector']")
    if (guestContainer) {
      const selectedIds = Array.from(guestContainer.querySelectorAll("input[name='user_ids[]']"))
        .map((el) => el.value.trim())
        .filter(Boolean)
      if (selectedIds.length > 0) {
        url.searchParams.set("selected_user_ids", selectedIds.join(","))
      }
    }

    try {
      const response = await fetch(url.toString(), {
        credentials: "same-origin",
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error("Search failed")
      }

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const resultsFrame = doc.querySelector("turbo-frame#search_results")

      if (resultsFrame) {
        this.resultsListTarget.innerHTML = resultsFrame.innerHTML
        this.loadingTarget.classList.add("hidden")
        if (this.hasErrorMessageTarget) {
          this.errorMessageTarget.classList.add("hidden")
        }
        if (this.hasInitialMessageTarget) {
          this.initialMessageTarget.classList.add("hidden")
        }
        this.updateResultsForForm()
        this.showResults()
      } else {
        this.showSearchError()
      }
    } catch (error) {
      console.error("Search error:", error)
      this.showSearchError()
    } finally {
      this.searchInFlight = false
    }
  }

  updateResultsForForm() {
    const shouldIntercept = (this.formModeValue && this.hasHiddenInputTarget) || this.guestSelectorValue
    this.resultLinkTargets.forEach((link, index) => {
      if (shouldIntercept) {
        const pointerHandler = (ev) => {
          ev.preventDefault()
        }
        const clickHandler = (ev) => {
          ev.preventDefault()
          ev.stopPropagation()
          this.selectUser(ev)
        }
        link.addEventListener("pointerdown", pointerHandler, { capture: true })
        link.addEventListener("click", clickHandler, { capture: true })
      }

      link.classList.remove("bg-accent-softer")
      if (index === this.selectedIndex) {
        link.classList.add("bg-accent-softer")
      }
    })
  }

  selectUser(event) {
    this.isSelecting = true

    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
      this.blurTimeout = null
    }

    const link = event.currentTarget || event.target.closest("[data-user-search-target='resultLink']")
    if (!link) {
      this.isSelecting = false
      return
    }

    const userId = link.dataset.userId
    const userName = link.dataset.userName || link.textContent.trim().split("\n")[0].trim()

    if (this.guestSelectorValue && userId && this.hasSearchInputTarget) {
      this.element.dispatchEvent(new CustomEvent("user-search:guest-selected", {
        detail: { userId, userName },
        bubbles: true
      }))
      this.searchInputTarget.value = ""
      this.resultsTarget.classList.add("hidden")
      this.selectedIndex = -1
      this.isSelecting = false
      return
    }

    if (this.formModeValue && userId && this.hasHiddenInputTarget && this.hasSearchInputTarget) {
      const guestContainer = this.element.closest("[data-controller~='lesson-guest-selector']")
      if (guestContainer) {
        const otherInputs = Array.from(guestContainer.querySelectorAll("input[name='user_ids[]']"))
          .filter((el) => el !== this.hiddenInputTarget)
        const alreadyInOther = otherInputs.some((el) => el.value === userId)
        if (alreadyInOther) {
          this.resultsTarget.classList.add("hidden")
          this.selectedIndex = -1
          this.isSelecting = false
          return
        }
      }

      this.hiddenInputTarget.value = userId
      this.searchInputTarget.value = userName
      this.element.dispatchEvent(new CustomEvent("user-search:selected", {
        detail: { userId, userName },
        bubbles: true
      }))

      this.resultsTarget.classList.add("hidden")
      this.selectedIndex = -1

      if (this.submitParentFormValue && this.hiddenInputTarget.form) {
        this.hiddenInputTarget.form.requestSubmit()
      }

      setTimeout(() => {
        this.isSelecting = false
      }, 100)
    } else {
      this.isSelecting = false
    }
  }

  navigateResults(direction) {
    if (this.resultLinkTargets.length === 0) return

    this.selectedIndex = Math.max(-1, Math.min(this.resultLinkTargets.length - 1, this.selectedIndex + direction))

    this.resultLinkTargets.forEach((link, index) => {
      link.classList.remove("bg-accent-softer")
      if (index === this.selectedIndex) {
        link.classList.add("bg-accent-softer")
        link.scrollIntoView({ block: "nearest" })
      }
    })
  }

  selectCurrentResult() {
    if (this.selectedIndex >= 0 && this.resultLinkTargets[this.selectedIndex]) {
      this.resultLinkTargets[this.selectedIndex].click()
    }
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
    this.positionResults()
    this.selectedIndex = -1

    if (!this.hasSearched && this.hasInitialMessageTarget) {
      this.initialMessageTarget.classList.remove("hidden")
      this.loadingTarget.classList.add("hidden")
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.classList.add("hidden")
      }
    }
  }

  hideResults() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }

    this.blurTimeout = setTimeout(() => {
      if (!this.isSelecting && !this.searchInFlight) {
        this.resultsTarget.classList.add("hidden")
        this.selectedIndex = -1
      }
    }, 200)
  }

  showLoading() {
    this.showResults()
    this.loadingTarget.classList.remove("hidden")
    if (this.hasInitialMessageTarget) {
      this.initialMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
    this.resultsListTarget.innerHTML = ""
  }

  showSearchError() {
    this.showResults()
    this.loadingTarget.classList.add("hidden")
    if (this.hasInitialMessageTarget) {
      this.initialMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
    this.resultsListTarget.innerHTML = ""
  }

  positionResults() {
    if (!this.hasSearchInputTarget || !this.hasResultsTarget) return

    const input = this.searchInputTarget
    const panel = this.resultsTarget
    const rect = input.getBoundingClientRect()
    const gap = 4
    const maxHeight = 240
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const openAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

    panel.style.left = `${rect.left}px`
    panel.style.width = `${rect.width}px`
    panel.style.maxHeight = `${maxHeight}px`

    if (openAbove) {
      panel.style.top = "auto"
      panel.style.bottom = `${window.innerHeight - rect.top + gap}px`
    } else {
      panel.style.top = `${rect.bottom + gap}px`
      panel.style.bottom = "auto"
    }
  }
}
